import { PanelLeft } from "lucide-react";
import {
  useEffect,
  useRef,
  useState,
  type Dispatch,
  type ReactElement,
  type ReactNode,
  type Ref,
  type RefObject,
  type SetStateAction,
} from "react";
import { Button, cn } from "@ndd/ui";
import { RAIL_TRANSITION, useIsSqueezeWidth } from "./shell-layout";
import {
  parseStoredSiderailCollapsed,
  siderailCollapsedStorageKey,
  serializeSiderailCollapsed,
} from "@/lib/siderail-state";

// The siderail's frame: the stored density choice, the mobile drawer, the
// toggles, and the drawer and scrim around the rail. Shell chrome, not page
// chrome: only a shell imports it (the guard in shell-layout.test.tsx).
// The drawer and scrim pin below the shell's own top bar, so each shell
// passes its `fixedTop` (NO-754).

// The RD's stored density choice, plus the squeeze a workspace drawer applies
// below 1280px through ShellSqueezeProvider.
export function useSiderailCollapse(userId: string): {
  collapsed: boolean;
  setAndStoreCollapsed: (next: boolean) => void;
  setSqueezed: Dispatch<SetStateAction<boolean>>;
  /** The parts `collapsed` is built from, for a shell that folds on its own
   *  terms (the single rail, NO-754). */
  storedCollapsed: boolean;
  squeezed: boolean;
  isSqueezeWidth: boolean;
} {
  const [storedCollapsed, setCollapsed] = useState<boolean>(() => {
    try {
      return parseStoredSiderailCollapsed(
        window.localStorage.getItem(siderailCollapsedStorageKey(userId))
      );
    } catch {
      return false;
    }
  });
  const [squeezed, setSqueezed] = useState(false);
  const isSqueezeWidth = useIsSqueezeWidth();
  // The RD's stored choice wins above 1280px; below it an open workspace
  // drawer collapses the siderail for the drawer's lifetime.
  const collapsed = storedCollapsed || (squeezed && isSqueezeWidth);

  const setAndStoreCollapsed = (next: boolean): void => {
    setCollapsed(next);

    try {
      window.localStorage.setItem(
        siderailCollapsedStorageKey(userId),
        serializeSiderailCollapsed(next)
      );
    } catch {
      // Storage can be unavailable (private mode); the session state still works.
    }
  };

  return {
    collapsed,
    setAndStoreCollapsed,
    setSqueezed,
    storedCollapsed,
    squeezed,
    isSqueezeWidth,
  };
}

export function useSiderailDrawer(
  pathname: string,
  isDesktop: boolean
): {
  open: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
  mainRef: RefObject<HTMLElement | null>;
  drawerRef: RefObject<HTMLDivElement | null>;
  mobileToggleRef: RefObject<HTMLButtonElement | null>;
} {
  const [open, setOpen] = useState(false);
  const mainRef = useRef<HTMLElement | null>(null);
  const drawerRef = useRef<HTMLDivElement | null>(null);
  const mobileToggleRef = useRef<HTMLButtonElement | null>(null);

  // The drawer is transient chrome: navigating anywhere closes it. The shell
  // owns the scroll container now, so a route change also resets it to the
  // top (the old per-page containers got this for free by remounting).
  useEffect(() => {
    setOpen(false);
    mainRef.current?.scrollTo(0, 0);
  }, [pathname]);
  // Crossing the breakpoint discards drawer state, so resizing back down
  // never resurrects a drawer nobody reopened.
  useEffect(() => {
    if (isDesktop) setOpen(false);
  }, [isDesktop]);
  // Below lg the open drawer behaves modally: focus moves into it, Escape or
  // the scrim dismisses, and focus returns to the toggle on close (the canvas
  // is inert meanwhile — see <main> in the shell).
  useEffect(() => {
    if (!open) return;

    drawerRef.current?.focus({ preventScroll: true });

    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === "Escape") setOpen(false);
    };

    window.addEventListener("keydown", onKeyDown);

    return () => {
      window.removeEventListener("keydown", onKeyDown);
      mobileToggleRef.current?.focus({ preventScroll: true });
    };
  }, [open]);

  return { open, setOpen, mainRef, drawerRef, mobileToggleRef };
}

// One control per viewport class so aria state stays static: the desktop
// button toggles siderail density, the mobile one the drawer. Visually they
// share the bar's fixed top-left home.
export function SiderailToggles({
  collapsed,
  drawerOpen,
  mobileToggleRef,
  onToggleCollapsed,
  onToggleDrawer,
}: {
  collapsed: boolean;
  drawerOpen: boolean;
  mobileToggleRef: Ref<HTMLButtonElement>;
  onToggleCollapsed: () => void;
  onToggleDrawer: () => void;
}): ReactElement {
  return (
    <>
      <Button
        variant="ghost"
        size="icon-sm"
        onClick={onToggleCollapsed}
        aria-expanded={!collapsed}
        aria-label={collapsed ? "Expand navigation" : "Collapse navigation"}
        title={collapsed ? "Expand navigation" : "Collapse navigation"}
        className="hidden h-8 w-8 shrink-0 text-muted-foreground lg:inline-flex"
      >
        <PanelLeft className="size-[17px]" />
      </Button>
      <Button
        ref={mobileToggleRef}
        variant="ghost"
        size="icon-sm"
        onClick={onToggleDrawer}
        aria-expanded={drawerOpen}
        aria-label={drawerOpen ? "Close navigation" : "Open navigation"}
        className="h-8 w-8 shrink-0 text-muted-foreground lg:hidden"
      >
        <PanelLeft className="size-[17px]" />
      </Button>
    </>
  );
}

// inert while closed below lg: a transform hides the drawer visually but would
// leave its links in the tab order and the AT tree.
export function SiderailDrawer({
  drawerRef,
  open,
  isDesktop,
  collapsed,
  fixedTop,
  children,
}: {
  drawerRef: Ref<HTMLDivElement>;
  open: boolean;
  isDesktop: boolean;
  collapsed: boolean;
  /** The shell's top-bar offset (`ShellTopBarGeometry.fixedTop`). */
  fixedTop: string;
  children: ReactNode;
}): ReactElement {
  return (
    <div
      ref={drawerRef}
      tabIndex={-1}
      inert={!isDesktop && !open}
      className={cn(
        `fixed bottom-0 left-0 ${fixedTop} z-40 w-[300px] overflow-hidden outline-none transition-transform`,
        RAIL_TRANSITION,
        open ? "translate-x-0 shadow-lg" : "-translate-x-[102%]",
        "lg:static lg:z-auto lg:shrink-0 lg:translate-x-0 lg:shadow-none lg:transition-[width]",
        collapsed ? "lg:w-16" : "lg:w-[300px]"
      )}
    >
      {children}
    </div>
  );
}

// Always rendered so the scrim fades instead of popping (mobile only).
export function SiderailScrim({
  open,
  fixedTop,
  onDismiss,
}: {
  open: boolean;
  /** The shell's top-bar offset (`ShellTopBarGeometry.fixedTop`). */
  fixedTop: string;
  onDismiss: () => void;
}): ReactElement {
  return (
    <div
      aria-hidden="true"
      onClick={onDismiss}
      className={cn(
        `fixed inset-x-0 bottom-0 ${fixedTop} z-30 bg-bark/25 transition-opacity lg:hidden`,
        RAIL_TRANSITION,
        open ? "opacity-100" : "pointer-events-none opacity-0"
      )}
    />
  );
}
